import React from 'react';

import { faCircleQuestion } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { UseFormReturn, FieldValues, FieldPath } from 'react-hook-form';

import {
    FormControl,
    FormControlStack,
    FormDescription,
    FormField,
    FormItem,
    FormLabel,
    FormMessage,
} from 'src/registry/ui/Form';
import { Textarea } from 'src/registry/ui/Textarea';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from 'src/registry/ui/Tooltip';

type TextareaFormFieldProps<TData extends FieldValues> = {
    form: UseFormReturn<TData>;
    name: FieldPath<TData>;
    label: string;
    className?: React.InputHTMLAttributes<HTMLInputElement>['className'];
    disabled?: boolean;
    inlineTitle?: string;
    required?: boolean;
    variant?: 'dialog' | 'default';
    tooltip?: string;
    description?: string;
};

export default function TextareaFormField<TData extends FieldValues>({
    variant = 'dialog',
    form,
    name,
    label,
    className,
    disabled,
    inlineTitle,
    required,
    tooltip,
    description,
}: TextareaFormFieldProps<TData>) {
    return (
        <FormField
            control={form.control}
            name={name}
            render={({ field }) => (
                <FormItem variant={variant}>
                    <FormLabel variant={variant} required={required}>
                        {label}
                    </FormLabel>
                    {tooltip && (
                        <TooltipProvider>
                            <Tooltip>
                                <TooltipTrigger type="button" className="mb-0 ml-1 text-gray-400">
                                    <FontAwesomeIcon icon={faCircleQuestion} />
                                </TooltipTrigger>
                                <TooltipContent>{tooltip}</TooltipContent>
                            </Tooltip>
                        </TooltipProvider>
                    )}
                    <FormControlStack>
                        <FormControl>
                            <Textarea
                                {...field}
                                inlineTitle={inlineTitle}
                                className={className}
                                disabled={disabled}
                                placeholder={`${label}...`}
                            />
                        </FormControl>
                        {description && <FormDescription>{description}</FormDescription>}
                        <FormMessage />
                    </FormControlStack>
                </FormItem>
            )}
        />
    );
}
